import { format, parseISO } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import { EditRideDetails } from '../../types/rides';

import { RideFormData } from '.';

function getDirtyFieldsForEditRide(originalValues: RideFormData, formValues: RideFormData) {
  let haveWaypointsChange = false;

  const originalWPS = originalValues.waypointsAttributes || [];
  const updatedWPS = formValues.waypointsAttributes || [];

  if (originalWPS?.length != updatedWPS?.length) {
    haveWaypointsChange = true;
  } else {
    for (let i = 0; i < originalWPS.length; i++) {
      const originalWP = originalWPS[i];
      const updatedWP = updatedWPS[i];

      const waypointUpdates = {
        ...(originalWP.anchor != updatedWP.anchor && { anchor: updatedWP.anchor }),
        ...(originalWP?.accountLocationAttributes?.placeId != updatedWP?.accountLocationAttributes?.placeId && {
          accountLocationAttributes: updatedWPS[i].accountLocationAttributes,
        }),
        ...(originalWP?.accountLocationAttributes?.streetAddressTwo !=
          updatedWP?.accountLocationAttributes?.streetAddressTwo && {
          accountLocationAttributes: updatedWPS[i].accountLocationAttributes,
        }),
        // Do not run this if anchor because the original anchor waypoints has no riders
        ...(!originalWP.anchor &&
          !isEqual(originalWP.passengerIdsStrings, updatedWP.passengerIdsStrings) && {
            passengerIdsStrings: updatedWP.passengerIdsStrings,
          }),
      };

      if (!isEmpty(waypointUpdates)) {
        haveWaypointsChange = true;
        break;
      }
    }
  }

  return {
    ...(originalValues.timeAnchor != formValues.timeAnchor && { timeAnchor: formValues.timeAnchor }),
    ...(originalValues.anchorTime != formValues.anchorTime && {
      anchorTime: formValues.anchorTime,
    }),
    ...(haveWaypointsChange && { waypointsAttributes: formValues.waypointsAttributes }),
    ...(originalValues.time != formValues.time && {
      time: formValues.time,
      anchorTime: formValues.anchorTime,
    }),
    ...(formValues.adjustmentReasonKey && {
      adjustmentReasonKey: formValues.adjustmentReasonKey,
    }),
  };
}

export function getDirtyFields(
  data: RideFormData,
  memoizedRideDetails: EditRideDetails,
  timeZone: string
): Partial<RideFormData> {
  const anchorTime = data.anchorTime || memoizedRideDetails.anchorTime;

  const updatedRide = {
    ...data,
    ...(anchorTime && {
      anchorTime: anchorTime,
    }),
  };

  const originalRide = {
    anchorTime: memoizedRideDetails.anchorTime,
    timeAnchor: memoizedRideDetails.timeAnchor,
    time: formatInTimeZone(memoizedRideDetails.anchorTime, timeZone, 'HH:mm'),

    waypointsAttributes:
      memoizedRideDetails.waypointsAttributes?.map((wp) => ({
        ...wp,
        anchor: !!wp.anchor,
        passengerIds: wp.passengerIdsStrings,
      })) || [],
  };

  return getDirtyFieldsForEditRide(originalRide, updatedRide);
}

export const mergeDateTime = (date: Date | string = '', timezone: string, time?: string): Date => {
  date = typeof date === 'string' ? parseISO(date) : date;
  const dateOnly = format(date, 'yyyy-MM-dd');

  return zonedTimeToUtc(`${dateOnly}T${time}`, timezone);
};
